import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, LoadingState, Toast } from "../ui";
import { appConstants } from "../_constants/app.constants";

/**
 * Landing page that the user reaches by clicking the link in the
 * "Confirma tu cuenta" email. Reads ?userId=…&token=… from the query string
 * and POSTs them to /api/auth/confirm-email. Renders OK / error / retry states.
 */
export default function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "error"
  const [message, setMessage] = useState("");
  const triedRef = useRef(false);

  const userId = (searchParams.get("userId") || "").trim();
  const token = (searchParams.get("token") || "").trim();

  useEffect(() => {
    if (triedRef.current) return;
    triedRef.current = true;

    if (!userId || !token) {
      setStatus("error");
      setMessage("El enlace de confirmación está incompleto. Vuelve a pedir uno nuevo desde la página de login.");
      return;
    }

    (async () => {
      try {
        await axios.post(`${appConstants.API_URL}/api/auth/confirm-email`, { userId, token });
        setStatus("ok");
        setMessage("Tu cuenta está confirmada. Ya puedes iniciar sesión.");
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "No se pudo confirmar el correo. El enlace puede haber caducado.";
        setStatus("error");
        setMessage(typeof msg === "string" ? msg : "Error al confirmar el correo.");
      }
    })();
  }, [userId, token]);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-cream via-white to-cream-deep px-4 py-12">
      <div className="pointer-events-none absolute inset-0 bg-noise opacity-30 mix-blend-multiply" aria-hidden />
      <div className="relative z-10 mx-auto flex min-h-[80vh] w-full max-w-md items-center">
        <Card className="w-full overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-gold via-gold-bright to-arena-bright" />
          <CardHeader className="pb-1 pt-6">
            <h1 className="font-display text-3xl font-extrabold text-ink">Confirmación de cuenta</h1>
            <p className="mt-1 text-sm text-ink-muted">Validando tu enlace de confirmación.</p>
          </CardHeader>
          <CardBody className="pb-6">
            {status === "loading" ? (
              <LoadingState label="Confirmando tu cuenta…" className="min-h-[10rem]" />
            ) : null}

            {status === "ok" ? (
              <>
                <Toast kind="success" className="mb-4">{message}</Toast>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate("/login", { replace: true })}
                >
                  Ir al login
                </Button>
              </>
            ) : null}

            {status === "error" ? (
              <>
                <Toast kind="error" className="mb-4">{message}</Toast>
                <div className="flex flex-col gap-2">
                  <Button as={RouterLink} to="/login" variant="primary" size="lg" className="w-full">
                    Volver al login
                  </Button>
                  <p className="text-center text-sm text-ink-muted">
                    Desde la página de login puedes pedir un nuevo enlace con «Reenviar email de confirmación».
                  </p>
                </div>
              </>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
